import type { SpeechMode } from './useSpeechRecording';

import { useCallback, useEffect, useRef } from 'react';

const PUSH_TO_TALK_ARM_DELAY_MS = 300;

export interface UseVoiceHotkeysOptions {
  /** 是否启用快捷键 */
  enabled: boolean;
  /** 当前录音模式 */
  mode: SpeechMode;
  /** 开始快速录入（按下 Option/Alt） */
  onStartPushToTalk: () => void;
  /** 结束快速录入（松开 Option/Alt） */
  onStopPushToTalk: (stopReason: 'key_up' | 'blur') => void;
  /** 切换到正常模式（Option/Alt + Space） */
  onSwitchToNormalMode: () => void;
}

/**
 * 语音快捷键 Hook
 *
 * 快捷键：
 * - macOS: Option（Alt）长按 300ms 开始快速录入；录音中 Option + Space 切换到正常模式
 * - Windows: Alt 长按 300ms 开始快速录入；录音中 Alt + Space 切换到正常模式
 *
 * 逻辑：
 * 1. Option/Alt 单键长按 >= 300ms → 开始 push-to-talk 录音
 * 2. 除 Option/Alt + Space 外，任何 Option/Alt 组合键都不触发语音
 * 3. 录音中 Option/Alt + Space → 切换到 normal 模式（录音继续）
 * 4. 松开 Option/Alt → 只在 push-to-talk 模式才自动转写
 */
export function useVoiceHotkeys(options: UseVoiceHotkeysOptions): void {
  const { enabled, mode, onStartPushToTalk, onStopPushToTalk, onSwitchToNormalMode } = options;

  const callbacksRef = useRef({ onStartPushToTalk, onStopPushToTalk, onSwitchToNormalMode });
  callbacksRef.current = { onStartPushToTalk, onStopPushToTalk, onSwitchToNormalMode };

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const modeRef = useRef(mode);
  modeRef.current = mode;

  const isAltPressedRef = useRef(false);
  const isArmedRef = useRef(false);
  const armTimerRef = useRef<number | null>(null);

  const resetArmingState = useCallback(() => {
    isAltPressedRef.current = false;
    if (armTimerRef.current) {
      window.clearTimeout(armTimerRef.current);
      armTimerRef.current = null;
    }
    isArmedRef.current = false;
  }, []);

  const cancelArming = useCallback(() => {
    if (armTimerRef.current) {
      window.clearTimeout(armTimerRef.current);
      armTimerRef.current = null;
    }
    isArmedRef.current = false;
  }, []);

  const armPushToTalkStart = useCallback(() => {
    cancelArming();
    isArmedRef.current = true;

    armTimerRef.current = window.setTimeout(() => {
      armTimerRef.current = null;

      if (!enabledRef.current) {
        isArmedRef.current = false;
        return;
      }
      if (!isArmedRef.current) {
        return;
      }
      if (!isAltPressedRef.current) {
        isArmedRef.current = false;
        return;
      }
      if (modeRef.current !== null) {
        isArmedRef.current = false;
        return;
      }

      isArmedRef.current = false;
      callbacksRef.current.onStartPushToTalk();
    }, PUSH_TO_TALK_ARM_DELAY_MS);
  }, [cancelArming]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Option/Alt + Space：只在录音中（push-to-talk）才切换到 normal
    if (e.altKey && e.code === 'Space') {
      cancelArming();
      if (modeRef.current === 'pushToTalk') {
        e.preventDefault();
        callbacksRef.current.onSwitchToNormalMode();
      }
      return;
    }

    // 按下 Option/Alt：进入“armed”状态，长按 >= 300ms 才开始录音
    if (e.key === 'Alt') {
      if (e.repeat) {
        return;
      }
      isAltPressedRef.current = true;

      if (enabledRef.current && modeRef.current === null) {
        armPushToTalkStart();
      }
      return;
    }

    // 除 Option/Alt + Space 外，任何组合键都不触发语音：在 armed 阶段按下其它键直接取消
    if (isArmedRef.current) {
      cancelArming();
    }
  }, [armPushToTalkStart, cancelArming]);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Alt') {
      return;
    }

    isAltPressedRef.current = false;

    // 松开 Option/Alt：如果还处于 armed（未真正开始录音）则取消
    if (isArmedRef.current) {
      cancelArming();
      return;
    }

    // 松开 Option/Alt：只在 push-to-talk 模式才结束录音
    if (modeRef.current === 'pushToTalk') {
      callbacksRef.current.onStopPushToTalk('key_up');
    }
  }, [cancelArming]);

  useEffect(() => {
    // 录音中也需要监听 keyup/blur 来结束录音，因此当 mode !== null 时也保持监听
    const shouldListen = enabled || mode !== null;

    if (!shouldListen) {
      resetArmingState();
      return;
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      resetArmingState();
    };
  }, [enabled, mode, handleKeyDown, handleKeyUp, resetArmingState]);

  // 窗口失焦时，如果在 push-to-talk 模式则自动结束
  useEffect(() => {
    const handleBlur = () => {
      resetArmingState();
      if (modeRef.current === 'pushToTalk') {
        callbacksRef.current.onStopPushToTalk('blur');
      }
    };

    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [resetArmingState]);
}
